import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import { isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import React from "react";
import { css } from "styled-components";

const isMGBW = isThemeActive(THEME_IDS.MGBW);

const enum fields {
    rightsReservedText = "rightsReservedText",
    links = "links",
}

interface FooterEndLineLink {
    fields: {
        text: string;
        destination: LinkFieldValue;
    };
}

interface Props extends WidgetProps {
    fields: {
        [fields.rightsReservedText]: string;
        [fields.links]: FooterEndLineLink[];
    };
}

export interface FooterEndLineStyle {
    container: InjectableCss;
    box?: InjectableCss;
    tittle?: InjectableCss;
    links?: InjectableCss;
    linkSeparator?: InjectableCss;
    heading?: InjectableCss;
}
export const footerEndLineStyle: FooterEndLineStyle = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            border-top: ${isMGBW ? `1px solid ${COLORS.border.main}` : "2px solid #686868"};
            ${isMGBW && css`
                width: 100%;
                padding: 12px 0;
            `}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        max-width: 100%;
                    `,
                    css`
                        flex-direction: column;
                        max-width: 100%;
                    `,
                    css`
                        flex-direction: column;
                        max-width: 100%;
                    `,
                    null,
                    null,
                ])}
            ${isMGBW && css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`padding: 16px 0 0 0;`,
                        css`padding: 16px 0 0 0;`,
                        css`padding: 16px 0 0 0;`,
                        null,
                        null,
                    ])}
            `}
        `,
    },
    tittle: {
        css: css`
            margin-top: ${isMGBW ? "0" : "14px"};
            margin-bottom: ${isMGBW ? "0" : "14px"};
            font-family: ${TYPOGRAPHY.footerEndLine.fontFamily};
            font-style: ${TYPOGRAPHY.footerEndLine.fontStyle};
            font-weight: ${TYPOGRAPHY.footerEndLine.weight};
            font-size: ${TYPOGRAPHY.footerEndLine.size};
            line-height: ${TYPOGRAPHY.footerEndLine.lineHeight};
            letter-spacing: ${TYPOGRAPHY.footerEndLine.letterSpacing};
            text-transform: uppercase;
            color: ${isMGBW ? COLORS.text.main : getColor("primary.contrast")};
            opacity: ${TYPOGRAPHY.footerEndLine.opacity};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    isMGBW ? css`
                        margin-top: 0;
                        margin-bottom: 10px;
                    ` : css`
                        margin-top: 24px;
                        margin-bottom: 14px;
                    `,
                    isMGBW ? css`
                        margin-top: 0;
                        margin-bottom: 10px;
                    ` : css`
                        margin-top: 24px;
                        margin-bottom: 14px;
                    `,
                    isMGBW ? css`
                        margin-top: 0;
                        margin-bottom: 10px;
                    ` : css`
                        margin-top: 24px;
                        margin-bottom: 14px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    links: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    linkSeparator: {
        css: css`
            margin: 0 4px;
        `,
    },
    heading: {
        css: css`
            margin-top: ${isMGBW ? "0" : "14px"};
            margin-bottom: ${isMGBW ? "0" : "14px"};
            font-family: ${TYPOGRAPHY.footerEndLine.fontFamily};
            font-style: ${TYPOGRAPHY.footerEndLine.fontStyle};
            font-weight: ${TYPOGRAPHY.footerEndLine.weight};
            font-size: ${TYPOGRAPHY.footerEndLine.size};
            line-height: ${TYPOGRAPHY.footerEndLine.lineHeight};
            letter-spacing: ${TYPOGRAPHY.footerEndLine.letterSpacing};
            text-align: right;
            text-transform: uppercase;

            span {
                color: ${isMGBW ? COLORS.text.main : getColor("primary.contrast")} !important;
                opacity: ${TYPOGRAPHY.footerEndLine.opacity};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    isMGBW ? css`
                        margin-top: 0;
                        margin-bottom: 0;
                        text-align: left;
                    ` : css`
                        margin-top: 0;
                        margin-bottom: 29px;
                        text-align: left;
                    `,
                    isMGBW ? css`
                        margin-top: 0;
                        margin-bottom: 0;
                        text-align: left;
                    ` : css`
                        margin-top: 0px;
                        margin-bottom: 29px;
                        text-align: left;
                    `,
                    isMGBW ? css`
                        margin-top: 0;
                        margin-bottom: 0;
                        text-align: left;
                    ` : css`
                        margin-top: 0px;
                        margin-bottom: 29px;
                        text-align: left;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};
const styles = footerEndLineStyle;

const FooterEndLine: React.FC<Props> = props => {
    const configuredLinks = props.fields.links || [];
    const { links: resolvedLinks } = useGetLinks(configuredLinks, link => link.fields.destination);
    const footerLinks = resolvedLinks
        .map((link, index) => ({
            text: configuredLinks[index].fields.text,
            url: link?.url,
        }))
        .filter(link => link.text && link.url);

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.tittle}>{props.fields.rightsReservedText}</StyledWrapper>
            <StyledWrapper {...styles.links}>
                {footerLinks.map((link, index) => (
                    <React.Fragment key={`${link.url}-${index}`}>
                        {index > 0 && <StyledWrapper {...styles.linkSeparator}>|</StyledWrapper>}
                        <Link {...styles.heading} href={link.url}>
                            {link.text}
                        </Link>
                    </React.Fragment>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: FooterEndLine,
    definition: {
        group: "Common",
        displayName: "FooterEndLine",
        fieldDefinitions: [
            {
                name: fields.rightsReservedText,
                displayName: "Tittle",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "text tip",
                placeholder: "text placeholder",
                isRequired: true,
                sortOrder: 0,
            },
            {
                name: fields.links,
                displayName: "Links",
                editorTemplate: "ListField",
                getDisplay: item => item.fields.text || "New link",
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "text",
                        displayName: "Link text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "destination",
                        displayName: "Destination",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: true,
                    },
                ],
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
